import React, { createContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const handleUserAuth = useCallback((userData, shouldRedirect = true) => {
    const normalizedUser = {
      ...userData,
      // Keep backward compatibility while transitioning
      IsAdmin: userData?.role === 'admin',
      role: userData?.role || 'student'
    };
    setUser(normalizedUser);
    
    if (shouldRedirect) {
      // Role-based redirection
      let redirectPath = '/dashboard';
      if (normalizedUser.role === 'admin') {
        redirectPath = '/admin/user';
      } else if (normalizedUser.role === 'instructor') {
        redirectPath = '/instructor/courses';
      } else if (normalizedUser.role === 'student') {
        redirectPath = '/courses';
      }
      
      const from = location.state?.from?.pathname || redirectPath;
      navigate(from, { replace: true });
    }
  }, [navigate, location.state]);

  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      setIsVerifying(false);
      return;
    }

    try {
      setIsVerifying(true);
      const response = await axiosInstance.get('/auth/verify-token');
      handleUserAuth(response.data.user, false);
    } catch (error) {
      localStorage.removeItem('userToken');
      setUser(null);
      if (!location.pathname.includes('/login')) {
        toast({
          title: 'انتهت الجلسة',
          description: 'انتهت صلاحية جلستك، يرجى تسجيل الدخول مرة أخرى',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  }, [handleUserAuth, toast, location.pathname]);

  const login = async (email, password) => {
    try {
      setIsVerifying(true);
      const response = await axiosInstance.post('/auth/login', { email, password });
      localStorage.setItem('userToken', response.data.token);
      handleUserAuth(response.data.user);
      toast({
        title: 'تم تسجيل الدخول بنجاح',
        description: `أهلاً بك، ${email}!`, 
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'خطأ في تسجيل الدخول',
        description: error.response?.data?.message || 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsVerifying(false);
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('خطأ في تسجيل الخروج:', error);
    } finally {
      localStorage.removeItem('userToken');
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      toast({
        title: 'تم إرسال البريد الإلكتروني',
        description: 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return true;
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || "حدث خطأ ما",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axiosInstance.post('/auth/reset-password', { token, newPassword });
      localStorage.setItem('userToken', response.data.token);
      handleUserAuth(response.data.user);
      toast({
        title: 'تمت إعادة تعيين كلمة المرور',
        description: 'تم تحديث كلمة المرور الخاصة بك بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return true;
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'رابط إعادة التعيين غير صالح أو انتهت صلاحيته',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  };

  useEffect(() => {
    verifyToken();
    const interval = setInterval(() => {
  // Only verify if user is still logged in and page is visible
  if (user && !document.hidden) {
    verifyToken().catch(error => {
      console.warn('Token verification failed:', error);
      // Don't immediately logout on network errors
    });
  }
}, 6800 * 60 * 1000);
    return () => clearInterval(interval);
  }, [verifyToken]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: isVerifying,
        login,
        logout,
        requestPasswordReset,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;